import { AllComponents } from "./interfaces/base";
import { getPageLocaleUrl } from "./utilities/helpers";
import {
  appendForwardedUrlParams,
  resolveBuilderLinkHref,
} from "./utilities/linkHref";
import { useAppSelector } from "../../store/hooks";
import RenderChildren from "./RenderChildren";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import BackgroundVideoComponent from "./BackgroundVideoComponent";
import useLazyLoad from "./utilities/useLazyLoad";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MegaMenuComponent from "./MegaMenuComponent";
import React from "react";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";

interface Props {
  id: number | string;
  uuid?: string | null;
  attributes: AllComponents["attributes"] & any; //Fix this
  childElements: AllComponents[];
  childWebpageComponentRelations: any;
  editor: JSX.Element;
}

const Section = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

export default function SectionComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
  editor,
}: Props) {
  const sectionRefElement = useRef<HTMLDivElement>(null);
  const isBuilder = document.getElementById("BuilderContent");
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = useAppSelector((state) => {
    return getComponentFromStructureMap(state.builder.structure.components, uuid ?? id);
  }) as any;
  const resolvedAttributes =
    component?.attributes && typeof component.attributes === "object"
      ? component.attributes
      : attributes && typeof attributes === "object"
      ? attributes
      : {};
  const resolvedStyles =
    resolvedAttributes?.styles && typeof resolvedAttributes.styles === "object"
      ? resolvedAttributes.styles
      : {};
  const resolvedComponent = component ?? {
    id,
    uuid: uuid ?? null,
    attributes: {
      ...resolvedAttributes,
      styles: resolvedStyles,
    },
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
  };
  const isEditing = matchesEditorTarget(component, editorId);
  const safeComponentId = uuid?.trim() || (id != null ? String(id) : "");
  const [backgroundStyles, setBackgroundStyles] =
    useState<React.CSSProperties>();
  const [borderColors, setBorderColors] = useState<any>();

  const colors = useAppSelector((state) => state.builder.colors);
  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [backgroundImage, setBackgroundImage] = useState(
    resolvedStyles.backgroundImage
      ? resolvedStyles.backgroundImage
      : ""
  );

  // if (classes && isBuilder) {
  //   console.log("classes", classes);

  //   if (classes.includes("d-none")) {
  //     classes = classes.replace("d-none", "b-d-none");
  //   } else if (classes.includes("d-sm-none")) {
  //     classes = classes.replace("d-sm-none", "b-d-sm-none");
  //   } else if (classes.includes("d-md-none")) {
  //     classes = classes.replace("d-md-none", "b-d-md-none");
  //   } else if (classes.includes("d-lg-none")) {
  //     classes = classes.replace("d-lg-none", "b-d-lg-none");
  //   } else if (classes.includes("d-xl-none")) {
  //     classes = classes.replace("d-xl-none", "b-d-xl-none");
  //   }

  //   console.log("updated calsses", classes);
  // }

  const [classes, setClasses] = useState(
    resolvedAttributes?.classes
  );

  useEffect(() => {
    if (!resolvedAttributes?.classes) return;

    let componentClasses = resolvedAttributes.classes;

    if (componentClasses && !isBuilder) {
      const classReplacements = [
        {
          original: "d-none",
          block: "d-sm-block",
        },
        {
          original: "d-sm-none",
          block: "d-md-block",
        },
        {
          original: "d-md-none",
          block: "d-lg-block",
        },
        {
          original: "d-lg-none",
          block: "d-xl-block",
        },
        {
          original: "d-xxl-none d-xl-none",
          block: "d-xxl-block ",
        },
      ];

      classReplacements.forEach(({ original, block }) => {
        componentClasses += ` ${block}`;
      });

      setClasses(componentClasses);
    }

    if (componentClasses && isBuilder) {
      // Add display: block!important, to sectionElementRef

      const sectionElementRef = sectionRefElement.current;

      if (sectionElementRef) {
        sectionElementRef.style.display = "block!important";
      }

      const classReplacements = [
        {
          original: "d-none",
          block: "d-sm-block",
        },
        {
          original: "d-sm-none",
          block: "d-md-block",
        },
        {
          original: "d-md-none",
          block: "d-lg-block",
        },
        {
          original: "d-lg-none",
          block: "d-xl-block",
        },
        {
          original: "d-xl-none d-xxl-none",
          block: "d-xxl-block ",
        },

        {
          original: "d-xxl-none",
          block: "d-xxl-block ",
        },
      ];

      classReplacements.forEach(({ original, block }) => {
        if (componentClasses.includes(original)) {
          componentClasses = componentClasses.replace(
            original,
            `b-${original}`
          );
        }

        componentClasses += ` ${block}`;
      });

      setClasses(componentClasses);
    }
  }, [
    resolvedAttributes?.classes,
    isBuilder,
    sectionRefElement.current,
    colors,
  ]);

  const handleMegaMenuOpen = (boolean) => {
    if (resolvedAttributes?.link?.type === "MegaMenu") {
      setMegaMenuOpen(boolean);
    }
  };

  const pages = useAppSelector((state) => state.builder.pages);

  const loaded = useLazyLoad(sectionRefElement, backgroundImage);

  useEffect(() => {
    if (!resolvedAttributes) return;

    const backgroundColor = resolvedAttributes.color_id
      ? colors?.find((x: any) => x.id === resolvedAttributes.color_id)?.color
      : resolvedStyles.backgroundColor
      ? resolvedStyles.backgroundColor
      : "";

    const backgroundColorHover = resolvedAttributes.hover_color_id
      ? colors?.find((x: any) => x.id === resolvedAttributes.hover_color_id)
          ?.color
      : resolvedStyles[":hover"] &&
        resolvedStyles[":hover"].backgroundColor
      ? resolvedStyles[":hover"].backgroundColor
      : "";

    const backgroundObject = {
      backgroundColor: `${backgroundColor}!important`,
      ":hover": {
        backgroundColor: `${backgroundColorHover} !important`,
      },
    };

    setBackgroundStyles(backgroundObject);
  }, [
    resolvedAttributes?.color_id,
    resolvedAttributes?.hover_color_id,
    resolvedStyles?.backgroundColor,
    resolvedStyles?.[":hover"]?.backgroundColor,
    colors,
  ]);

  useEffect(() => {
    // Change border color values

    const borderColorValues = {
      borderTopColor: resolvedAttributes?.borderTopColorId
        ? colors?.find(
            (x: any) => x.id === resolvedAttributes?.borderTopColorId
          )?.color
        : resolvedStyles?.borderTopColor
        ? resolvedStyles?.borderTopColor
        : "",
      borderBottomColor: resolvedAttributes?.borderBottomColorId
        ? colors?.find(
            (x: any) => x.id === resolvedAttributes?.borderBottomColorId
          )?.color
        : resolvedStyles?.borderBottomColor
        ? resolvedStyles?.borderBottomColor
        : "",
      borderLeftColor: resolvedAttributes?.borderLeftColorId
        ? colors?.find(
            (x: any) => x.id === resolvedAttributes?.borderLeftColorId
          )?.color
        : resolvedStyles?.borderLeftColor
        ? resolvedStyles?.borderLeftColor
        : "",
      borderRightColor: resolvedAttributes?.borderRightColorId
        ? colors?.find(
            (x: any) => x.id === resolvedAttributes?.borderRightColorId
          )?.color
        : resolvedStyles?.borderRightColor,
    };

    setBorderColors(borderColorValues);
  }, [
    resolvedAttributes,
    resolvedStyles,
    resolvedAttributes?.borderBottomColorId,
    resolvedAttributes?.borderLeftColorId,
    resolvedAttributes?.borderRightColorId,
    resolvedAttributes?.borderTopColorId,
    colors,
  ]);

  useEffect(() => {
    if (resolvedStyles.backgroundImage) {
      setBackgroundImage(resolvedStyles.backgroundImage);
    }
  }, [resolvedStyles.backgroundImage]);

  let href = resolveBuilderLinkHref(attributes.link, pages.list, (page) =>
    getPageLocaleUrl(page, languages)
  );
  if (attributes.keepUrlParams) {
    href = appendForwardedUrlParams(
      href,
      attributes.link?.type,
      location.search,
      location.pathname
    );
  }
  const hasHref = href !== "" && href !== undefined && href.trim() !== "";

  const handleSectionClick = () => {
    if (attributes.link?.type === "MegaMenu") {
      handleMegaMenuOpen(!megaMenuOpen);
    }

    if (isBuilder || !href || href.trim() === "") {
      return;
    }

    const resolvedHref = href;

    if (attributes.link?.target === "_blank") {
      window.open(resolvedHref, "_blank");
      return;
    }

    if (
      attributes.link?.type === "Web_out" ||
      attributes.link?.type === "Phone" ||
      attributes.link?.type === "Email"
    ) {
      window.location.href = resolvedHref;
      return;
    }

    navigate(resolvedHref);
    window.scrollTo(0, 0);
  };

  if (attributes && attributes.classes == "row") {
    let style: any = {};
    if (attributes.content_width_size) {
      style = {
        maxWidth: attributes.content_width_size + attributes.content_width_unit,
      };
    }

    return (
      <>
        <div
          ref={sectionRefElement}
          className="container g-0"
          style={style}
          id={
            attributes.anchor_id
              ? attributes.anchor_id.includes("#")
                ? attributes.anchor_id.split("#")[1]
                : attributes.anchor_id
              : ""
          }
        >
          <Section
            id={
              safeComponentId
                ? safeComponentId
                : attributes.anchor_id
                  ? attributes.anchor_id
                  : ""
            }
            className={
              resolvedAttributes &&
              resolvedAttributes.classes +
                ` g-0 builder-component relative builder-component__section ${id}-section
              ` +
                (isEditing ? " builder-component--active" : "")
            }
            data-style={
              attributes && attributes.styles
                ? {
                    ...attributes.styles,

                    ...backgroundStyles,
                    backgroundColor: backgroundStyles?.backgroundColor,

                    backgroundImage: "none",
                  }
                : {}
            }
            data-component-type="section"
            style={{
              ...attributes.styles,
              zIndex: "inherit",
              position: "relative",
              backgroundImage: !isBuilder
                ? loaded
                  ? loaded.map((src) => `url("${src}")`).join(", ")
                  : "none"
                : resolvedStyles.backgroundImage,
              borderLeftColor: borderColors?.borderLeftColor,
              borderRightColor: borderColors?.borderRightColor,
              borderTopColor: borderColors?.borderTopColor,
              borderBottomColor: borderColors?.borderBottomColor,
              cursor: hasHref ? "pointer" : "default",
            }}
            data-href={href}
            onClick={handleSectionClick}
          >
            <BackgroundVideoComponent componentKey={uuid} />

            {attributes && attributes.text}
            <RenderChildren
              key={safeComponentId + "children"}
              childWebpageComponentRelations={childWebpageComponentRelations}
            />
          </Section>
        </div>
      </>
    );
  }
  if (attributes && attributes.classes == "container-fluid") {
    let style: any = {};
    if (attributes.content_width_size) {
      style = {
        maxWidth: attributes.content_width_size + attributes.content_width_unit,
      };
    } else {
      style = {
        maxWidth: "100%",
      };
    }

    return (
      <Section
        ref={sectionRefElement}
        id={safeComponentId}
        className={
          " g-0 builder-component relative builder-component__section" +
          (isEditing ? " builder-component--active" : "")
        }
        data-style={
          attributes && attributes.styles
            ? {
                ...attributes.styles,

                ...backgroundStyles,
                backgroundColor: backgroundStyles?.backgroundColor,
                backgroundImage: "none",
              }
            : {}
        }
        style={{
          position: "relative",
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          zIndex: "inherit",
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
          cursor: hasHref ? "pointer" : "default",
        }}
        data-component-type="section"
        data-href={href}
        onClick={handleSectionClick}
      >
        <BackgroundVideoComponent componentKey={uuid} />
        {attributes && attributes.text}
        <div className="container g-0" style={style}>
          <RenderChildren
            key={safeComponentId + "children"}
            childWebpageComponentRelations={childWebpageComponentRelations}
          />
        </div>
      </Section>
    );
  }

  function generateLink(link: any) {
    if (!attributes) return;

    const href = resolveBuilderLinkHref(link, pages.list, (page) =>
      getPageLocaleUrl(page, languages)
    );

    return link.type === "Page" ? (
      <Link
        className="button-link"
        key={link.text + link.type + href}
        to={href || "#"} // Avoid redirecting when editing
        target={link.type !== "None" ? link.target : "_self"}
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
      ></Link>
    ) : (
      <a
        className="button-link"
        key={link.text + link.type + href}
        href={href}
        target={
          link.type !== "None" && link.type !== "" ? link.target : "_self"
        }
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
        onClick={() => {}}
        style={{
          zIndex: 8,
        }}
      ></a>
    );
  }

  return (
    <Section
      ref={sectionRefElement}
      id={safeComponentId}
      data-style={
        attributes && attributes.styles
          ? {
              ...attributes.styles,
              ...backgroundStyles,
              backgroundColor: backgroundStyles?.backgroundColor,
              backgroundImage: "none",
            }
          : {}
      }
      className={
        classes +
        " g-0  builder-component COLUMN relative builder-component__section" +
        (isEditing ? " builder-component--active" : "")
      }
      data-component-type="section"
      style={{
        backgroundImage: !isBuilder
          ? loaded
            ? loaded.map((src) => `url("${src}")`).join(", ")
            : "none"
          : resolvedStyles.backgroundImage,

        cursor: hasHref ? "pointer" : "default",

        display: "block!important",
        borderLeftColor: borderColors?.borderLeftColor,
        borderRightColor: borderColors?.borderRightColor,
        borderTopColor: borderColors?.borderTopColor,
        borderBottomColor: borderColors?.borderBottomColor,
      }}
      data-href={href}
      onClick={handleSectionClick}
      onMouseEnter={(e) => {
        e.preventDefault();
        handleMegaMenuOpen(true);

        // setMegaMenuOpen(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();

        handleMegaMenuOpen(false);

        // setMegaMenuOpen(false);
      }}
    >
      {attributes &&
        attributes.link &&
        attributes.link.type === "MegaMenu" &&
        attributes.link.template_id !== null && (
          <>
            <MegaMenuComponent
              id={attributes.link.template_id}
              parentId={id}
              menuOpen={megaMenuOpen}
              setMegaMenuOpen={setMegaMenuOpen}
              leftPosition={attributes.link?.left_position}
              topPosition={attributes.link?.top_position}
              activateType={attributes.link?.activate_type}
            />
          </>
        )}

      {attributes &&
        attributes.link &&
        attributes.link.type === "Dropdown" &&
        attributes.links &&
        attributes.links.length > 0 && (
          <>
            <div className="col-subnav">
              {attributes.links && attributes.links.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {attributes.links.map((item, i) => generateLink(item))}
                </div>
              )}
            </div>
          </>
        )}

      <BackgroundVideoComponent componentKey={uuid} />

      {attributes && attributes.text}
      <RenderChildren
        key={safeComponentId + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />
    </Section>
  );
}
